<template>
  <div v-if="show" class="modal-overlay" @click.self="handleCancel">
    <div class="modal-content bulgarian-invoice-modal">
      <div class="modal-header">
        <div class="header-icon">
          <font-awesome-icon icon="fa-solid fa-gas-pump" class="icon-gas-pump" />
        </div>
        <div class="header-content">
          <h3>{{ t('bulgarianInvoice.title') }}</h3>
          <p class="modal-subtitle">
            {{ t(invoiceCount === 1 ? 'bulgarianInvoice.subtitleSimpleSingle' : 'bulgarianInvoice.subtitleSimple', { count: invoiceCount }) }}
          </p>
        </div>
        <button @click="handleCancel" class="btn btn-icon">
          <font-awesome-icon icon="fa-solid fa-xmark" />
        </button>
      </div>
      
      <div class="modal-body">
        <div class="info-section">
          <div class="info-icon">
            <font-awesome-icon icon="fa-solid fa-info-circle" />
          </div>
          <p>
            {{ t('bulgarianInvoice.infoText') }}
          </p>
        </div>

        <div class="form-group">
          <label for="bulgarian-role-select">
            <font-awesome-icon icon="fa-solid fa-building" />
            {{ t('bulgarianInvoice.myCompanyRole') }}
          </label>
          <select 
            v-model="selectedRole" 
            id="bulgarian-role-select" 
            class="form-select"
          >
            <option value="buyer">
              <font-awesome-icon icon="fa-solid fa-cart-shopping" />
              {{ t('bulgarianInvoice.buyerOption') }}
            </option>
            <option value="seller">
              <font-awesome-icon icon="fa-solid fa-hand-holding-dollar" />
              {{ t('bulgarianInvoice.sellerOption') }}
            </option>
          </select>
          <p class="help-text">
            <strong>{{ t('bulgarianInvoice.buyerHelp').split(':')[0] }}:</strong> {{ t('bulgarianInvoice.buyerHelp').split(':')[1] }}<br>
            <strong>{{ t('bulgarianInvoice.sellerHelp').split(':')[0] }}:</strong> {{ t('bulgarianInvoice.sellerHelp').split(':')[1] }}
          </p>
        </div>

        <div class="form-group">
          <label for="bulgarian-payment-select">
            <font-awesome-icon icon="fa-solid fa-credit-card" />
            {{ t('bulgarianInvoice.paymentMethod') }}
          </label>
          <select 
            v-model="selectedPaymentMethod" 
            id="bulgarian-payment-select" 
            class="form-select"
          >
            <option value="Bank Transfer">{{ t('bulgarianInvoice.paymentBankTransfer') }}</option>
            <option value="Cash">{{ t('bulgarianInvoice.paymentCash') }}</option>
            <option value="Credit Card">{{ t('bulgarianInvoice.paymentCreditCard') }}</option>
            <option value="Check">{{ t('bulgarianInvoice.paymentCheck') }}</option>
            <option value="Online Payment">{{ t('bulgarianInvoice.paymentOnline') }}</option>
            <option value="Deferred Payment">{{ t('bulgarianInvoice.paymentDeferred') }}</option>
            <option value="Partial Payment">{{ t('bulgarianInvoice.paymentPartial') }}</option>
            <option value="Other">{{ t('bulgarianInvoice.paymentOther') }}</option>
          </select>
        </div>

        <div class="summary-box">
          <div class="summary-title">
            <font-awesome-icon icon="fa-solid fa-file-invoice" />
            {{ t('bulgarianInvoice.summary') }}
          </div>
          <div class="summary-content">
            <div class="summary-row">
              <span>{{ t('bulgarianInvoice.totalCompanies') }}</span>
              <strong>{{ companyCount }}</strong>
            </div>
            <div class="summary-row">
              <span>{{ t('bulgarianInvoice.totalInvoices') }}</span>
              <strong>{{ invoiceCount }}</strong>
            </div>
            <div class="summary-row">
              <span>{{ t('bulgarianInvoice.totalItems') }}</span>
              <strong>{{ itemCount }}</strong>
            </div>
            <div class="summary-row">
              <span>{{ t('bulgarianInvoice.grandTotal') }}</span>
              <strong>{{ grandTotal }} BGN</strong>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="handleCancel" class="btn-cancel">
          <font-awesome-icon icon="fa-solid fa-xmark" />
          {{ t('bulgarianInvoice.cancel') }}
        </button>
        <button @click="handleConfirm" class="btn-confirm" :disabled="processing">
          <font-awesome-icon v-if="!processing" icon="fa-solid fa-check" />
          <div v-else class="loading-spinner-small"></div>
          {{ processing ? t('bulgarianInvoice.processing') : t('bulgarianInvoice.confirmProcess') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  bulgarianData: {
    type: Object,
    default: null
  },
  processing: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['confirm', 'cancel']);

const selectedRole = ref('buyer');
const selectedPaymentMethod = ref('Bank Transfer');

const invoiceCount = computed(() => {
  return props.bulgarianData?.summary?.total_invoices || 0;
});

const companyCount = computed(() => {
  return props.bulgarianData?.summary?.total_companies || 0;
});

const itemCount = computed(() => {
  return props.bulgarianData?.summary?.total_items || 0;
});

const grandTotal = computed(() => {
  return props.bulgarianData?.summary?.grand_total?.toFixed(2) || '0.00';
});

const handleConfirm = () => {
  emit('confirm', {
    role: selectedRole.value,
    paymentMethod: selectedPaymentMethod.value
  });
};

const handleCancel = () => {
  emit('cancel');
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
  padding: 20px;
  overflow-y: auto;
}

.bulgarian-invoice-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: scaleIn 0.3s ease;
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 24px 20px 24px;
  border-bottom: 1px solid var(--border);
  position: relative;
}

.header-icon {
  background: var(--primary);
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.icon-gas-pump {
  /* Optional: remove animation for cleaner look */
}

.header-content {
  flex: 1;
}

.header-content h3 {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
}

.modal-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-section {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: #fef2f2;
  border-left: 4px solid var(--primary);
  border-radius: 8px;
}

.info-icon {
  color: var(--primary);
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 2px;
}

.info-section p {
  margin: 0;
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1.6;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-group label svg {
  font-size: 16px;
  color: var(--primary);
}

.form-select {
  padding: 12px 14px;
  border: 2px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.form-select:hover {
  border-color: var(--primary);
}

.help-text {
  margin: 0;
  padding: 12px;
  background: #f8fafc;
  border-radius: 6px;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.6;
}

.help-text strong {
  color: var(--text-primary);
}

.summary-box {
  background: white;
  border: 2px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}

.summary-title {
  background: var(--primary);
  color: white;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.summary-row span {
  color: var(--text-secondary);
}

.summary-row strong {
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 600;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--border);
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-cancel {
  background: white;
  color: var(--text-primary);
  border: 2px solid var(--border);
}

.btn-cancel:hover {
  background: #f8fafc;
  border-color: var(--text-secondary);
}

.btn-confirm {
  background: var(--primary);
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  background: #0284c7;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
